import { useEffect, useReducer, useRef } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { ComboboxMulti } from "@/components/ui/combobox_mutli";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { useListAgtechCommodities, type AgtechCommodity } from "@/lib/api/agtech/lookup";
import {
  GenerateAgtechShippedReportValidation,
  useGenerateAgtechShippedReport,
} from "@/lib/api/agtechReports";
import { useListCustomers } from "@/lib/api/customer";
import {
  convertFormatStringToLocalTimezone,
  formatDateWithoutTimezoneToFormat,
} from "@/lib/api/date";
import type { Customer } from "@/lib/api/reports";
import { downloadFromUrl } from "@/lib/browser/download";
import { getMessageOrDefault } from "@/lib/prelude";
import { SHIPPED_REPORT_PRESETS, type ShippedReportPreset } from "./shippedReportPresets";

interface ShippedReportFormValues {
  customerIds: string[];
  commodityId: string;
  shipTo: string;
  shipDateStart: Date | undefined;
  shipDateEnd: Date | undefined;
  title: string;
  tabName: string;
}

interface ShippedReportState {
  isOpen: boolean;
  isGenerating: boolean;
  selectedPresetId: string;
  dateRangeKey: number;
  downloadUrl: string | null;
  values: ShippedReportFormValues;
}

type ShippedReportAction =
  | { type: "setOpen"; value: boolean }
  | { type: "selectCustom" }
  | { type: "applyPreset"; presetId: string; values: ShippedReportFormValues }
  | { type: "setCustomerIds"; value: string[] }
  | { type: "setCommodityId"; value: string }
  | { type: "setShipTo"; value: string }
  | { type: "setShipDateRange"; from: Date | undefined; to: Date | undefined }
  | { type: "setTitle"; value: string }
  | { type: "setTabName"; value: string }
  | { type: "clear" }
  | { type: "generationStarted" }
  | { type: "generationFinished" }
  | { type: "generated"; url: string };

const EMPTY_FORM_VALUES: ShippedReportFormValues = {
  customerIds: [],
  commodityId: "",
  shipTo: "",
  shipDateStart: undefined,
  shipDateEnd: undefined,
  title: "",
  tabName: "",
};

const defaultPresetId = "gevo-yearly-report";
const defaultPreset = SHIPPED_REPORT_PRESETS.find(
  (preset: typeof SHIPPED_REPORT_PRESETS[number]) => preset.id === defaultPresetId,
)!;
const INITIAL_STATE: ShippedReportState = {
  isOpen: false,
  isGenerating: false,
  selectedPresetId: defaultPresetId,
  dateRangeKey: 0,
  downloadUrl: null,
  values: presetToFormValues(defaultPreset),
};

function updateValues(
  state: ShippedReportState,
  values: ShippedReportFormValues,
): ShippedReportState {
  return {
    ...state,
    selectedPresetId: "custom",
    downloadUrl: null,
    values,
  };
}

function shippedReportReducer(
  state: ShippedReportState,
  action: ShippedReportAction,
): ShippedReportState {
  switch (action.type) {
    case "setOpen":
      return { ...state, isOpen: action.value };
    case "selectCustom":
      return { ...state, selectedPresetId: "custom" };
    case "applyPreset":
      return {
        ...state,
        selectedPresetId: action.presetId,
        dateRangeKey: state.dateRangeKey + 1,
        downloadUrl: null,
        values: action.values,
      };
    case "setCustomerIds":
      return updateValues(state, { ...state.values, customerIds: action.value });
    case "setCommodityId":
      return updateValues(state, { ...state.values, commodityId: action.value });
    case "setShipTo":
      return updateValues(state, { ...state.values, shipTo: action.value });
    case "setShipDateRange":
      return updateValues(state, {
        ...state.values,
        shipDateStart: action.from,
        shipDateEnd: action.to,
      });
    case "setTitle":
      return updateValues(state, { ...state.values, title: action.value });
    case "setTabName":
      return updateValues(state, { ...state.values, tabName: action.value });
    case "clear":
      return {
        ...INITIAL_STATE,
        isOpen: state.isOpen,
        dateRangeKey: state.dateRangeKey + 1,
      };
    case "generationStarted":
      return { ...state, isGenerating: true, downloadUrl: null };
    case "generationFinished":
      return { ...state, isGenerating: false };
    case "generated":
      return { ...state, downloadUrl: action.url };
    default:
      return state;
  }
}

function presetDate(value: string | undefined): Date | undefined {
  return value
    ? convertFormatStringToLocalTimezone(value, "YYYY-MM-DD")
    : undefined;
}

/** Maps a preset's snake_case fields directly to camelCase form values (no customer lookups). */
function presetToFormValues(preset: ShippedReportPreset): ShippedReportFormValues {
  return {
    customerIds: [],
    commodityId: preset.commodity_id ?? "",
    shipTo: "",
    shipDateStart: presetDate(preset.ship_date_start),
    shipDateEnd: presetDate(preset.ship_date_end),
    title: preset.title ?? "",
    tabName: preset.tab_name ?? "",
  };
}

function resolvePresetValues(
  preset: ShippedReportPreset,
  customers: Customer[],
): ShippedReportFormValues {
  const customerIds = customers
    .filter((customer: Customer): boolean => preset.customer_long_names.includes(customer.long_name))
    .map((customer: Customer): string => customer.id);
  const shipTo = customers.find(
    (customer: Customer): boolean => customer.long_name === preset.ship_to_long_name,
  )?.client_id ?? "";

  return {
    ...presetToFormValues(preset),
    customerIds,
    shipTo,
  };
}

function formatDateForApi(date: Date): string {
  return formatDateWithoutTimezoneToFormat(date, "YYYY-MM-DD");
}

export function ShippedTicketsReportCard() {
  const { toast } = useToast();
  const [state, dispatch] = useReducer(shippedReportReducer, INITIAL_STATE);
  const customersResult = useListCustomers({ minimal: true });
  const commoditiesResult = useListAgtechCommodities();
  const generateReport = useGenerateAgtechShippedReport();
  const initialPresetResolved = useRef(false);

  const customers: Customer[] = customersResult.data?.data ?? [];
  const commodities: AgtechCommodity[] = commoditiesResult.data?.data ?? [];

  useEffect(() => {
    if (initialPresetResolved.current) return;
    if (customers.length === 0) return;
    if (state.selectedPresetId === "custom") return;

    const preset = configuredPresets.find(
      (item: ShippedReportPreset): boolean => item.id === state.selectedPresetId,
    );
    if (!preset) return;

    initialPresetResolved.current = true;
    dispatch({
      type: "applyPreset",
      presetId: state.selectedPresetId,
      values: resolvePresetValues(preset, customers),
    });
  }, [customers]);
  const configuredPresets = SHIPPED_REPORT_PRESETS.filter(
    (preset: ShippedReportPreset): boolean => preset.label.trim().length > 0,
  );
  const presetOptions = [
    { value: "custom", label: "Custom" },
    ...configuredPresets.map((preset: ShippedReportPreset): { value: string; label: string } => ({
      value: preset.id,
      label: preset.label,
    })),
  ];
  const customerOptions = customers.map((customer: Customer): { value: string; label: string } => ({
    value: customer.id,
    label: customer.long_name,
  }));
  const shipToOptions = customers.map((customer: Customer): { value: string; label: string } => ({
    value: customer.client_id,
    label: customer.long_name,
  }));
  const commodityOptions = commodities.map(
    (commodity: AgtechCommodity): { value: string; label: string } => ({
      value: commodity.commodity_id,
      label: commodity.name,
    }),
  );

  const handlePresetChange = (presetId: string): void => {
    if (presetId === "custom") {
      dispatch({ type: "selectCustom" });
      return;
    }

    const preset = configuredPresets.find(
      (item: ShippedReportPreset): boolean => item.id === presetId,
    );
    if (!preset) return;

    dispatch({
      type: "applyPreset",
      presetId,
      values: resolvePresetValues(preset, customers),
    });
  };

  const handleGenerate = async (): Promise<void> => {
    if (state.isGenerating) return;

    const validationResult = GenerateAgtechShippedReportValidation.safeParse({
      filters: {
        ...(state.values.customerIds.length > 0
          ? { customer_ids: state.values.customerIds.join(",") }
          : {}),
        ...(state.values.commodityId ? { commodity_id: state.values.commodityId } : {}),
        ...(state.values.shipTo ? { ship_to: state.values.shipTo } : {}),
        ...(state.values.shipDateStart
          ? { ship_date_start: formatDateForApi(state.values.shipDateStart) }
          : {}),
        ...(state.values.shipDateEnd
          ? { ship_date_end: formatDateForApi(state.values.shipDateEnd) }
          : {}),
      },
      ...(state.values.title || state.values.tabName
        ? {
            report_info: {
              ...(state.values.title ? { title: state.values.title } : {}),
              ...(state.values.tabName ? { tab_name: state.values.tabName } : {}),
            },
          }
        : {}),
    });

    if (!validationResult.success) {
      toast({
        title: "Invalid report options",
        description: validationResult.error.issues[0]?.message ?? "Please correct the report options.",
        variant: "destructive",
      });
      return;
    }

    dispatch({ type: "generationStarted" });
    try {
      const result = await generateReport.mutateAsync(validationResult.data);
      if (!result.success) {
        toast({
          title: "Report generation failed",
          description: result.message || "Failed to generate shipped tickets report.",
          variant: "destructive",
        });
        return;
      }

      dispatch({ type: "generated", url: result.data.link });
      await downloadFromUrl(result.data.link, result.data.filename);
      toast({ title: "Shipped tickets report generated." });
    } catch (error: unknown) {
      console.error("Failed to generate shipped tickets report", error);
      toast({
        title: "Report generation failed",
        description: getMessageOrDefault(error, "Failed to generate shipped tickets report."),
        variant: "destructive",
      });
    } finally {
      dispatch({ type: "generationFinished" });
    }
  };

  return (
    <Dialog
      open={state.isOpen}
      onOpenChange={(open: boolean) => dispatch({ type: "setOpen", value: open })}
    >
      <DialogTrigger asChild>
        <button type="button" className="text-left">
          <Card className="h-full shadow-sm transition-colors hover:bg-muted/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Download className="h-5 w-5 text-primary" />
                Shipped Tickets Report
              </CardTitle>
              <CardDescription>
                Generate a split-level shipped tickets report using a preset or custom filters.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <span className="text-sm font-medium text-primary">Configure report</span>
            </CardContent>
          </Card>
        </button>
      </DialogTrigger>

      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Shipped Tickets Report</DialogTitle>
          <DialogDescription>
            Choose a preset or customize the report filters and workbook names.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          <div className="grid space-y-1">
            <Label>Preset</Label>
            <Combobox
              options={presetOptions}
              value={state.selectedPresetId}
              onValueChange={handlePresetChange}
              placeholder="Select preset..."
              isModal
              dwimOnChange
              virtualizeList
              popoverClassName="w-[400px] md:w-[550px]"
            />
          </div>

          <div className="grid space-y-1">
            <Label>Customers</Label>
            <ComboboxMulti
              options={customerOptions}
              value={state.values.customerIds}
              onValueChange={(value: string[]) => dispatch({ type: "setCustomerIds", value })}
              placeholder="Select customers..."
              isModal
              dwimOnChange
              virtualizeList
              className="w-full"
              popoverClassName="w-[400px] md:w-[550px]"
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="grid space-y-1">
              <Label>Commodity</Label>
              <Combobox
                options={commodityOptions}
                value={state.values.commodityId}
                onValueChange={(value: string) => dispatch({ type: "setCommodityId", value })}
                placeholder="Select commodity..."
                isModal
                dwimOnChange
                virtualizeList
                className="w-full"
                popoverClassName="w-[400px] md:w-[550px]"
              />
            </div>
            <div className="grid space-y-1">
              <Label>Ship To</Label>
              <Combobox
                options={shipToOptions}
                value={state.values.shipTo}
                onValueChange={(value: string) => dispatch({ type: "setShipTo", value })}
                placeholder="Select destination..."
                isModal
                dwimOnChange
                virtualizeList
                className="w-full"
                popoverClassName="w-[400px] md:w-[550px]"
              />
            </div>
          </div>

          <div className="grid space-y-1">
            <Label>Ship Date Range</Label>
            <DateRangePicker
              key={state.dateRangeKey}
              value={{
                from: state.values.shipDateStart,
                to: state.values.shipDateEnd,
              }}
              onChange={(range: { from?: Date; to?: Date }) => dispatch({
                type: "setShipDateRange",
                from: range.from,
                to: range.to,
              })}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="grid space-y-1">
              <Label htmlFor="shipped-report-title">Filename</Label>
              <Input
                id="shipped-report-title"
                value={state.values.title}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => dispatch({
                  type: "setTitle",
                  value: event.target.value,
                })}
                placeholder="Tickets Shipped"
              />
              <p className="text-xs text-muted-foreground">The .xlsx extension is added automatically.</p>
            </div>
            <div className="grid space-y-1">
              <Label htmlFor="shipped-report-tab-name">Excel Tab Name</Label>
              <Input
                id="shipped-report-tab-name"
                value={state.values.tabName}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => dispatch({
                  type: "setTabName",
                  value: event.target.value,
                })}
                placeholder="Shipped"
                maxLength={31}
              />
              <p className="text-xs text-muted-foreground">Name of Excel Summary Tab</p>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => dispatch({ type: "clear" })}
            disabled={state.isGenerating}
          >
            Clear
          </Button>
          <div className="flex items-center gap-2">
            {state.downloadUrl && (
              <Button variant="outline" asChild>
                <a href={state.downloadUrl} target="_blank" rel="noreferrer">Download Last</a>
              </Button>
            )}
            <Button type="button" onClick={handleGenerate} disabled={state.isGenerating}>
              <Download className="mr-2 h-4 w-4" />
              {state.isGenerating ? "Generating..." : "Generate Excel"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
